import { useUserEventLogger } from '@repo/tracking-service';
import { Stepper } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessTakeoverEmployeeQuery, TakingOverEmployeesType } from '@/__generated__/businessTakeoverEmployeeQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CTA, CheckList, CheckListGroup, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { birthToYYMD } from '@/components/survey/common/utils';
import { upsertTakingOverEmployees } from '@/graphql/mutation/survey/business';
import { takeoverEmployeeQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import { employeesAtom } from '@/lib/stores/survey/common';

const answers: { value: TakingOverEmployeesType; label: string }[] = [
  { value: 'yes', label: '네, 있었어요' },
  { value: 'no', label: '아니요' },
  { value: 'unknown', label: '잘 모르겠어요' }
];

export const TakeoverEmployeeContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessTakeoverEmployeeQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessTakeoverEmployeeQuery>(takeoverEmployeeQuery, queryRef);
  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertTakingOverEmployees);

  const employees = useAtomValue(employeesAtom);
  const [selectedAnswer, setSelectedAnswer] = useState<TakingOverEmployeesType | undefined>();
  const [selectedEmployeeSet, setSelectedEmployeeSet] = useState<Set<string>>(new Set());

  const business = getSurveyBusinessAnswer?.businessAnswer?.[0];

  useEffect(() => {
    if (business?.hasTakingOverEmployees && business.takingOverEmployees) {
      setSelectedAnswer(business.hasTakingOverEmployees);
      const employeeIdSet = new Set(business.takingOverEmployees.map((employee) => employee.indvTin));
      setSelectedEmployeeSet(employeeIdSet);
    }
  }, [business, setSelectedAnswer]);

  const toggleEmployee = (id: string) => {
    if (selectedEmployeeSet.has(id)) {
      selectedEmployeeSet.delete(id);
    } else {
      selectedEmployeeSet.add(id);
    }
    setSelectedEmployeeSet(new Set(selectedEmployeeSet));
  };

  return (
    <>
      <SurveyTopNavigation needConfirm={selectedAnswer === 'yes'} leftClickEvent={'survey_takeover-employee_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <ProgressBar step={2} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_takeover-employee_confirm', eventAttributes);

            upsertBusinessAnswer(
              {
                hasTakingOverEmployees: selectedAnswer,
                takingOverEmployees:
                  selectedAnswer === 'yes'
                    ? employees
                        .filter(({ id }) => selectedEmployeeSet.has(id))
                        .map(({ id, name, workedYears, birthday }) => ({
                          indvTin: id,
                          ieNm: name,
                          attrYears: workedYears,
                          birthday: birthday || undefined
                        }))
                    : []
              },
              {
                onCompleted: () => {
                  router.push(SURVEY_PAGES.사업장_근무여부);
                }
              }
            );
          }}
        >
          <article>
            <section>
              <h1 className={common.content_title}>고용을 승계받은 직원이 있었나요?</h1>
              <p className={common.section_title}>하나라도 해당되면 직원을 승계받은 경우예요</p>

              <div className={'mb-8'}>
                <Stepper.Label stepCount={1} title={'인수한 회사의 당시 직원을 그대로 고용한 경우'} />
                <Stepper.Label stepCount={2} title={'특수관계인이 운영하는 회사에서 근무했던 직원을 내 사업장에서 고용한 경우'} />
                <Stepper.Content hasStepLine={false}>
                  특수관계인에는 배우자(이혼, 재혼, 사실혼 포함)와 6촌 이내의 혈족, 4촌 이내의 인척이 포함돼요.
                </Stepper.Content>
              </div>

              <CheckListGroup>
                {answers.map(({ value, label }) => (
                  <CheckList
                    key={value}
                    value={value as string}
                    checked={selectedAnswer === value}
                    onChange={() => {
                      sendClickEvent(`survey_takeover-employee_${value}`, eventAttributes);

                      setSelectedAnswer(value);
                    }}
                  >
                    {label}
                  </CheckList>
                ))}
              </CheckListGroup>
            </section>

            {selectedAnswer === 'yes' && (
              <section className={'pt-6'}>
                <h2 className={common.section_title}>
                  아래에서 고용 승계받은 직원을 <br />
                  모두 선택해주세요
                </h2>
                <CheckListGroup>
                  {employees?.map((employee) => (
                    <CheckList
                      key={employee.id}
                      value={employee.id}
                      checked={selectedEmployeeSet.has(employee.id)}
                      onChange={() => {
                        sendClickEvent('survey_takeover-employee_select', eventAttributes);

                        toggleEmployee(employee.id);
                      }}
                    >
                      {employee.name} ({birthToYYMD(employee.birthday || '')})
                    </CheckList>
                  ))}
                </CheckListGroup>
              </section>
            )}
          </article>

          {!!selectedAnswer && (
            <CTA type={'submit'} disabled={(selectedAnswer === 'yes' && selectedEmployeeSet.size === 0) || isLoading}>
              {(function () {
                if (selectedAnswer === 'yes') {
                  const count = selectedEmployeeSet.size;
                  return count === 0 ? '해당 직원을 모두 선택해주세요' : `${count}명 선택 완료`;
                }
                return '다음';
              })()}
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
